import { Trans, useLingui } from "@lingui/react/macro";
import { Check, X } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@/utils/shadcn-ui";

import type { DataModel } from "../../../../models/data-model";
import type { Column, DataTableProps } from "./types";

import { hasPermission } from "../../../../constants/permissions";
import { APIDataModel } from "../../../../models/api-data-model";
import { Button } from "../../button";
import { Form } from "../../form/form";
import { Spinner } from "../../spinner";
import styles from "../../table.module.css";
import { Cell } from "../cells/cell";
import { DualScrollbarContainer } from "../dual-scrollbar-container";
import { EditRowDialog } from "../edit-row-dialog";
import { buildColumns } from "./column-builder";
import { editableFields, isEditable } from "./editable-cell";
import { FilterToolbar } from "./filter-toolbar/filter-toolbar";
import { clearField, getMultiSelected } from "./filter-toolbar/tanstack-bridge";
import { MultiEditToolbarActions } from "./multi-edit-toolbar-actions";
import { PaginationControl } from "./pagination-control";
import { RowIndexProvider } from "./row-index-context";
import { RowsSelector } from "./rows-selector";
import { getStickyCellProps } from "./sticky-cell-style";
import { filterFieldForColumn } from "./types";
import { useDataTable } from "./use-data-table";
import { useModelSync } from "./use-model-sync";
import { usePinnedOffsets } from "./use-pinned-offsets";
import { useScrollShadows } from "./use-scroll-shadows";

/**
 * Row class for `rowClassName` marking a row as done/handled with a soft green
 * wash. Exported (rather than left to the caller's own CSS) because the tint
 * has to reach the sticky cells, which carry their own opaque background.
 */
export const ROW_TINT_SUCCESS = styles.rowTintSuccess;

export const DataTable = observer(function DataTable<
  T extends object,
  K extends keyof T = keyof T,
>({
  model,
  columns: columnsProp,
  itemKeyField,
  foot,
  verticalAlign,
  editMode = "row",
  isLoading,
  fillHeight,
  filterNoun,
  filterActions,
  canSelect,
  selectionHint,
  selectDisabled,
  readOnly,
  preselected,
  onSelect,
  onAdd,
  onEdit,
  onDelete,
  confirmDelete,
  onMultiEdit,
  rowsPerPage,
  persistState,
  persistStateKey,
  initialColumnFilters,
  filterDeps,
  patchAlwaysIncludeKeys,
  renderEditDialog,
  rowClassName,
  getSubRows,
  serverPagination,
}: DataTableProps<T, K>) {
  const effectiveRowsPerPage = rowsPerPage ?? 25;
  const filteredColumns = useMemo(
    () =>
      columnsProp.filter(
        (col): col is Column<T> =>
          !!col &&
          (col.requiredPermission == null ||
            !model.permissions ||
            hasPermission(model.permissions, col.requiredPermission)),
      ),
    [columnsProp, model.permissions],
  );

  const derivedFilters = useMemo(
    () => filteredColumns.flatMap((col) => filterFieldForColumn(col) ?? []),
    [filteredColumns],
  );

  const loading =
    (model instanceof APIDataModel && model.loading) || !!isLoading;
  const multiEdit = !!onMultiEdit;
  const showEditColumn =
    (multiEdit || !!onEdit) && model.canEdit() && editMode !== "cell";
  const showDeleteColumn = !!onDelete && model.canDelete();
  const showHistoryButton = model.hasHistory && model.canShowHistory;
  const showPagination = effectiveRowsPerPage !== Number.MAX_VALUE;
  const expandable = !!getSubRows;

  const [dialogEditingItem, setDialogEditingItem] = useState<T | undefined>();

  useModelSync({ model, canSelect, preselected, onSelect, itemKeyField });

  const {
    columns: builtColumns,
    pinnedStart,
    pinnedEnd,
  } = useMemo(
    () =>
      buildColumns({
        columns: filteredColumns,
        model,
        canSelect,
        selectionHint,
        selectDisabled,
        readOnly,
        expandable,
        showEditColumn,
        showDeleteColumn,
        editMode,
        multiEdit,
        onDelete,
        confirmDelete,
        onDialogEdit: (item: T) => setDialogEditingItem(item),
        onEdit,
      }),
    [
      filteredColumns,
      model,
      canSelect,
      selectionHint,
      selectDisabled,
      readOnly,
      expandable,
      showEditColumn,
      showDeleteColumn,
      editMode,
      multiEdit,
      onDelete,
      confirmDelete,
      onEdit,
    ],
  );

  // Read selection in the observer body so this re-renders (and therefore
  // re-pins) whenever the model's selection changes. Pin only ids that exist
  // in the data: `getTopRows()` below resolves each pinned id via TanStack's
  // `getRow`, which throws if the row is absent — e.g. a `preselected` id while
  // the data is still loading (empty `model.data`). Once the data arrives this
  // re-runs and pins the row. Filtering against the full `model.data` (not the
  // visible rows) preserves `keepPinnedRows` for selections filtered out of view.
  const pinnedTopIds = canSelect
    ? (() => {
        const presentIds = new Set(
          (model.data ?? []).map((row) => String(row[itemKeyField])),
        );
        return model.selected
          .map((id) => String(id))
          .filter((id) => presentIds.has(id));
      })()
    : [];

  const table = useDataTable<T>({
    model,
    filteredColumns,
    builtColumns,
    itemKeyField,
    rowsPerPage: effectiveRowsPerPage,
    persistState: !!persistState,
    persistStateKey,
    canSelect,
    pinnedTopIds,
    pinnedStart,
    pinnedEnd,
    initialColumnFilters,
    filterDeps,
    getSubRows,
    serverPagination,
  });

  // Prune stale selections from multi-filters that depend on another filter
  // (e.g. Region depends on Country). When the dependency clears, the field's
  // editor disappears but the underlying value would otherwise keep filtering
  // and counting toward the active badge.
  const columnFiltersKey = JSON.stringify(table.state.columnFilters);
  useEffect(() => {
    for (const field of derivedFilters) {
      if (
        field.kind === "multi" &&
        field.disabled?.(table) &&
        getMultiSelected(table, field).length > 0
      ) {
        clearField(table, field);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `table` is a stable instance and `table.state` reads aren't reactive deps; columnFiltersKey captures the relevant state
  }, [columnFiltersKey, derivedFilters]);

  const tableRef = useRef<HTMLTableElement>(null);
  useScrollShadows(tableRef);
  const pinnedOffsets = usePinnedOffsets(
    tableRef,
    table
      .getVisibleLeafColumns()
      .map((col) => `${col.id}:${col.getIsPinned()}`)
      .join(","),
  );

  const rows = [
    ...table.getTopRows(),
    ...table.getCenterRows(),
    ...table.getBottomRows(),
  ];
  // `nth-child` striping would flip parity at every expanded sub-row, so an
  // expandable table stripes by top-level row instead and a sub-row wears its
  // parent's stripe — a row and its sub-rows read as one band.
  const stripes = expandable ? stripeParities(rows) : undefined;
  const visibleColumnCount = table.getVisibleLeafColumns().length;
  // The add-row footer renders one input cell per column, so it has to follow
  // the visible set or its cells drift out of line with the body's.
  const visibleDataColumns = table
    .getVisibleLeafColumns()
    .flatMap((column) => column.columnDef.meta?.column ?? []);
  const footContent = typeof foot === "function" ? foot(table) : foot;

  return (
    <div
      data-slot="data-table"
      className={cn(
        "flex flex-col items-start w-full",
        fillHeight && "flex-1 min-h-0",
      )}
    >
      <Form
        className={cn(
          "w-full",
          fillHeight ? "flex flex-col flex-1 min-h-0" : "h-full",
        )}
        onSubmit={async () => {
          if (multiEdit && model.multiEditDrafts.size > 0) {
            const patches = model.generateMultiPatches(patchAlwaysIncludeKeys);
            if (patches.length > 0 && onMultiEdit) {
              const result = await onMultiEdit(patches);
              const failedIds = new Set(
                result && "failedIds" in result ? (result.failedIds ?? []) : [],
              );
              // Keep drafts for failed rows so the user can fix and retry;
              // clear everything else (successful patches + dirty-but-no-change drafts).
              for (const id of Array.from(model.multiEditDrafts.keys())) {
                if (!failedIds.has(id)) {
                  model.stopMultiEditing(id);
                }
              }
            } else {
              model.stopAllMultiEditing();
            }
            return;
          }
          // `editingItemId` is set for row/cell editing, and also when an
          // external flow inline-edits a row in dialog mode (e.g. medications
          // "Change dosage"). Normal dialog editing goes through the dialog and
          // leaves editingItemId undefined, so this block is skipped for it.
          if (model.editingItemId !== undefined) {
            // Look up by id — page/sort/filter may have moved or hidden the
            // row between edit-start and submit.
            const id = String(model.editingItemId);
            const item = table.getCoreRowModel().rowsById[id]?.original;
            const patch = model.generatePatch(patchAlwaysIncludeKeys);
            if (item && patch && Object.keys(patch).length > 0) {
              await onEdit?.(patch, item);
            }
            model.stopEditing();
          }
        }}
      >
        {derivedFilters.length > 0 ||
        filterActions ||
        filterNoun ||
        multiEdit ? (
          <FilterToolbar
            table={table}
            fields={derivedFilters}
            noun={filterNoun}
            actions={
              multiEdit ? (
                <>
                  {filterActions}
                  <MultiEditToolbarActions model={model} />
                </>
              ) : (
                filterActions
              )
            }
            isLoading={loading}
          />
        ) : null}
        {/* Relative wrapper is viewport-bounded (unlike the table, which can
            overflow it), so the loading overlay always covers the visible area. */}
        <div
          className={cn(
            "relative w-full",
            fillHeight && "flex-1 min-h-0 flex flex-col",
          )}
        >
          <DualScrollbarContainer
            className={styles.tableContainer}
            fillHeight={fillHeight}
          >
            <table
              ref={tableRef}
              className={cn(
                styles.table,
                stripes && styles.explicitStripes,
                verticalAlign === "top" && styles.verticalAlignTop,
                fillHeight && styles.fillHeight,
              )}
            >
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const isLeafHeader = header.subHeaders.length === 0;
                      const sticky = getStickyCellProps(
                        header.column,
                        pinnedOffsets,
                        { tagColumnId: isLeafHeader },
                      );
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          data-group-header={isLeafHeader ? undefined : true}
                          data-group-start={
                            header.column.columnDef.meta?.groupStart ||
                            undefined
                          }
                          className={
                            ALIGN_CLASS[
                              header.column.columnDef.meta?.column?.align ??
                                "center"
                            ]
                          }
                          {...sticky.dataAttrs}
                          style={{ width: header.getSize(), ...sticky.style }}
                        >
                          {header.isPlaceholder ? null : (
                            <table.FlexRender header={header} />
                          )}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>

              <tbody inert={loading}>
                {rows.length === 0 ? (
                  <tr style={{ pointerEvents: "none" }}>
                    <td
                      colSpan={visibleColumnCount}
                      style={{
                        height: "120px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      {!loading && <Trans>No data</Trans>}
                    </td>
                  </tr>
                ) : (
                  rows.map((row, pageRowIndex) => (
                    <RowIndexProvider key={row.id} value={pageRowIndex}>
                      <tr
                        data-stripe={stripes?.[pageRowIndex]}
                        className={cn(
                          canSelect &&
                            model.idAttr != null &&
                            model.isSelected(row.original[model.idAttr]) &&
                            styles.selected,
                          rowClassName?.(row.original),
                        )}
                      >
                        {row.getVisibleCells().map((cell) => {
                          const col = cell.column;
                          const colMeta = col.columnDef.meta?.column;
                          const attr = colMeta?.attr;
                          const isEditableCell =
                            editMode === "cell" &&
                            colMeta != null &&
                            attr != null &&
                            isEditable(colMeta, row.original);
                          const isActivelyEditing =
                            isEditableCell &&
                            model.idAttr != null &&
                            model.editingItemId ===
                              row.original[model.idAttr] &&
                            model.canEditField(attr);
                          const clickable =
                            isEditableCell && !isActivelyEditing;
                          const action = colMeta?.cellAction?.(row.original);
                          const sticky = getStickyCellProps(col, pinnedOffsets);
                          return (
                            <td
                              key={cell.id}
                              data-group-start={
                                colMeta && col.columnDef.meta?.groupStart
                                  ? true
                                  : undefined
                              }
                              className={cn(
                                ALIGN_CLASS[colMeta?.align ?? "center"],
                                clickable &&
                                  "text-brand-primary cursor-pointer",
                                action && "cursor-pointer",
                                colMeta?.className,
                              )}
                              {...sticky.dataAttrs}
                              style={sticky.style}
                              onClick={
                                clickable
                                  ? () =>
                                      model.startEditingItem(row.original, [
                                        attr,
                                      ])
                                  : (action ?? undefined)
                              }
                            >
                              <table.FlexRender cell={cell} />
                            </td>
                          );
                        })}
                      </tr>
                    </RowIndexProvider>
                  ))
                )}
              </tbody>

              {(footContent || (onAdd && editMode === "row")) && (
                <tfoot>
                  {footContent ??
                    (onAdd && (
                      <AddRowFooter
                        model={model}
                        columns={visibleDataColumns}
                        colSpan={visibleColumnCount}
                        onAdd={onAdd}
                      />
                    ))}
                </tfoot>
              )}
            </table>
          </DualScrollbarContainer>
          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60">
              <Spinner size={24} />
            </div>
          )}
        </div>
      </Form>

      {(showPagination || showHistoryButton) && (
        <div className="flex flex-wrap items-center gap-x-10 gap-y-3 mt-2">
          {showPagination && (
            <>
              <PaginationControl table={table} />
              <RowsSelector table={table} />
            </>
          )}

          {showHistoryButton && (
            <Button onClick={() => model.toggleHistory()}>
              <Trans>Toggle history</Trans>
            </Button>
          )}
        </div>
      )}

      {dialogEditingItem &&
        (renderEditDialog ? (
          renderEditDialog({
            item: dialogEditingItem,
            onClose: () => setDialogEditingItem(undefined),
            onSave: async (patch) => {
              await onEdit?.(patch as Partial<T>, dialogEditingItem);
            },
          })
        ) : (
          <EditRowDialog
            columns={filteredColumns}
            item={dialogEditingItem}
            patchAlwaysIncludeKeys={patchAlwaysIncludeKeys}
            onClose={() => setDialogEditingItem(undefined)}
            onSave={async (patch) => {
              await onEdit?.(patch as Partial<T>, dialogEditingItem);
            }}
          />
        ))}
    </div>
  );
  // observer<T extends object>() loses the generic — cast it back so callers
  // get type inference on T and the key field K.
}) as <T extends object, K extends keyof T = keyof T>(
  props: DataTableProps<T, K>,
) => React.JSX.Element;

function stripeParities(rows: { depth: number }[]): ("odd" | "even")[] {
  let topLevel = -1;
  return rows.map((row) => {
    if (row.depth === 0) topLevel++;
    return topLevel % 2 === 0 ? "odd" : "even";
  });
}

const ALIGN_CLASS = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
} as const;

const AddRowFooter = observer(function AddRowFooter<T extends object>({
  model,
  columns,
  colSpan,
  onAdd,
}: {
  model: DataModel<T>;
  columns: Column<T>[];
  colSpan: number;
  onAdd: (added: Partial<T>) => void | Promise<unknown>;
}) {
  const { t } = useLingui();
  const fields = useMemo(() => editableFields(columns, {} as T), [columns]);

  return (
    <tr>
      {model.editingIndex === -1 ? (
        <>
          {columns.map((col, i) => (
            <td key={i}>
              <Cell
                item={model.editingDraft}
                patch={model.editingDraft}
                column={col}
                index={() => -1}
              />
            </td>
          ))}
          <td>
            <Button
              onClick={async () => {
                await onAdd(model.editingDraft);
                model.stopEditing();
              }}
              variant="subtle"
              aria-label={t`Add`}
            >
              <Check className="size-4" />
            </Button>
          </td>
          <td>
            <Button
              onClick={() => model.stopEditing()}
              variant="subtle"
              aria-label={t`Cancel`}
            >
              <X className="size-4" />
            </Button>
          </td>
        </>
      ) : (
        <td colSpan={colSpan}>
          <Button variant="subtle" onClick={() => model.startAdding(fields)}>
            <Trans>+ Add</Trans>
          </Button>
        </td>
      )}
    </tr>
  );
});
